import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Link,
  Stack
} from '@mui/material';

interface EventRef {
  id: string;
  eventName: string;
  eventDateTime?: string | null;
}

interface CandidateItemRef {
  id: string;
  name: string;
}

interface OutgoingInterestRef {
  id: string;
  eventID: string;
  targetItemID?: string;
  targetItemName?: string;
  targetOwnerUserID?: string;
  targetOwnerName?: string;
  targetOwnerUserName?: string | null;
  candidateItems?: CandidateItemRef[];
  status?: string;
  note?: string;
}

interface IncomingInterestRef {
  id: string;
  eventID: string;
  requestingUserID?: string;
  requestingUserName?: string;
  candidateItems?: CandidateItemRef[];
  status?: string;
}

interface ActivityItem {
  name: string;
  listedEvents?: EventRef[];
  outgoingInterestCount?: number;
  incomingInterestCount?: number;
  outgoingInterests?: OutgoingInterestRef[];
  incomingInterests?: IncomingInterestRef[];
}

interface ItemActivityModalProps {
  open: boolean;
  onClose: () => void;
  item: ActivityItem | null;
}

const formatEventLabel = (event?: EventRef | null) => event?.eventName || event?.id || 'Unknown event';

const summarizeCandidateItems = (candidateItems?: CandidateItemRef[]) => {
  if (!candidateItems || candidateItems.length === 0) return 'no additional items';
  return candidateItems.map((candidate) => candidate.name || candidate.id).join(', ');
};

const ItemActivityModal: React.FC<ItemActivityModalProps> = ({ open, onClose, item }) => {
  const navigate = useNavigate();
  if (!item) return null;

  const openProfile = (userId?: string) => {
    if (!userId) return;
    onClose();
    navigate(`/users/${userId}`);
  };

  const openEvent = (eventId?: string) => {
    if (!eventId) return;
    onClose();
    navigate(`/events/${eventId}`);
  };

  const openItemInEvent = (eventId?: string, targetItemId?: string) => {
    if (!eventId || !targetItemId) return;
    onClose();
    navigate(`/events/${eventId}/items/${targetItemId}`);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Activity for {item.name}</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>Listed in Events</Typography>
          {(item.listedEvents || []).length === 0 ? (
            <Typography color="text.secondary">This item is not currently listed in any events.</Typography>
          ) : (
            <List>
              {(item.listedEvents || []).map((event) => (
                <ListItem
                  key={event.id}
                  disableGutters
                  secondaryAction={<Button size="small" onClick={() => openEvent(event.id)}>Open Event</Button>}
                >
                  <ListItemText
                    primary={formatEventLabel(event)}
                    secondary={event.eventDateTime ? new Date(event.eventDateTime).toLocaleString() : 'No event date'}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>Offers I&apos;ve Made Using This Item ({item.outgoingInterestCount || 0})</Typography>
          {(item.outgoingInterests || []).length === 0 ? (
            <Typography color="text.secondary">No outgoing trade interests using this item yet.</Typography>
          ) : (
            <List>
              {(item.outgoingInterests || []).map((interest) => (
                <ListItem key={interest.id} alignItems="flex-start" disableGutters sx={{ py: 1.5 }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="subtitle2">
                          You offered {item.name} for {interest.targetItemName || interest.targetItemID || 'Unknown Item'}
                        </Typography>
                        <Chip size="small" label={interest.status || 'active'} />
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Event: {interest.eventID}
                        </Typography>
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
                          <Button size="small" onClick={() => openEvent(interest.eventID)}>Open Event</Button>
                          <Button size="small" onClick={() => openItemInEvent(interest.eventID, interest.targetItemID)}>Open Item</Button>
                        </Stack>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.75 }}>
                          <Typography variant="body2" color="text.secondary">
                            Trader: {interest.targetOwnerName || interest.targetOwnerUserID || 'Unknown Trader'}
                          </Typography>
                          {interest.targetOwnerUserID && (
                            <Link
                              component="button"
                              underline="hover"
                              sx={{ fontSize: '0.8rem' }}
                              onClick={() => openProfile(interest.targetOwnerUserID)}
                            >
                              View Profile
                            </Link>
                          )}
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Offer summary: {item.name} offered for {interest.targetItemName || interest.targetItemID || 'Unknown Item'} using {summarizeCandidateItems(interest.candidateItems)}.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Offered with:
                        </Typography>
                        {(interest.candidateItems || []).length > 0 ? (
                          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
                            {(interest.candidateItems || []).map((candidate) => (
                              <Chip key={candidate.id} size="small" variant="outlined" label={candidate.name || candidate.id} />
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            No offered items listed.
                          </Typography>
                        )}
                        {interest.note && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Note: {interest.note}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box>
          <Typography variant="h6" gutterBottom>Offers Others Made To Me ({item.incomingInterestCount || 0})</Typography>
          {(item.incomingInterests || []).length === 0 ? (
            <Typography color="text.secondary">No incoming trade interests on this item yet.</Typography>
          ) : (
            <List>
              {(item.incomingInterests || []).map((interest) => (
                <ListItem key={interest.id} alignItems="flex-start" disableGutters sx={{ py: 1.5 }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="subtitle2">{interest.requestingUserName || interest.requestingUserID || 'Unknown User'} wants {item.name}</Typography>
                        <Link
                          component="button"
                          underline="hover"
                          sx={{ fontSize: '0.8rem' }}
                          onClick={() => openProfile(interest.requestingUserID)}
                        >
                          View Profile
                        </Link>
                        <Chip size="small" label={interest.status || 'active'} />
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Event: {interest.eventID}
                        </Typography>
                        <Button size="small" sx={{ mt: 0.5 }} onClick={() => openEvent(interest.eventID)}>Open Event</Button>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Offer summary: {interest.requestingUserName || interest.requestingUserID || 'Unknown trader'} offered {summarizeCandidateItems(interest.candidateItems)} for {item.name}.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Candidate items:
                        </Typography>
                        {(interest.candidateItems || []).length > 0 ? (
                          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
                            {(interest.candidateItems || []).map((candidate) => (
                              <Chip key={candidate.id} size="small" variant="outlined" label={candidate.name || candidate.id} />
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            None
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ItemActivityModal;
